import React, { useState, useEffect, useCallback, useRef } from 'react';
import ExportCsvButton from '../components/ExportCsvButton';
import { Helmet } from 'react-helmet';
import { useDomain } from '../contexts/DomainContext';
import { useAuth } from '../contexts/AuthContext';
import {
  MagnifyingGlassIcon,
  PlusIcon,
  TrashIcon,
  PlayIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
  XMarkIcon,
  ChevronLeftIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline';
import { runSingleScan, getGeoEngines, getGeoUsage, getGeoState, saveGeoState } from '../services/geoService';

const AI_ENGINES = [
  { key: 'chatgpt', label: 'ChatGPT' },
  { key: 'perplexity', label: 'Perplexity' },
  { key: 'google_ai', label: 'Google AI' },
  { key: 'claude', label: 'Claude' },
  { key: 'bing', label: 'Bing' },
  { key: 'you', label: 'You.com' },
];

const STATUS_COLORS = {
  active: 'bg-green-100 text-green-700',
  scanning: 'bg-blue-100 text-blue-700',
  error: 'bg-red-100 text-red-700',
  pending: 'bg-yellow-100 text-yellow-700'
};

// Breadcrumb
const GEOBreadcrumb = ({ items }) => (
  <nav aria-label="GEO breadcrumb" className="mb-4">
    <ol className="flex items-center space-x-2 text-sm" itemScope itemType="https://schema.org/BreadcrumbList">
      {items.map((item, i) => (
        <li key={i} className="flex items-center" itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
          {i > 0 && <span className="mx-2 text-gray-400">/</span>}
          {item.href ? (
            <a href={item.href} className="text-indigo-600 hover:text-indigo-800" itemProp="item">
              <span itemProp="name">{item.label}</span>
            </a>
          ) : (
            <span className="text-gray-600 font-medium" itemProp="name">{item.label}</span>
          )}
          <meta itemProp="position" content={String(i + 1)} />
        </li>
      ))}
    </ol>
  </nav>
);

export default function GEOTracking() {
  const { selectedDomain } = useDomain();
  const { user } = useAuth();
  const [tracked, setTracked] = useState([]);
  const stateDomain = (selectedDomain || '').trim();
  const loadedFor = useRef(null);
  const saveTimer = useRef(null);
  // Load this domain's tracked prompts from the server.
  useEffect(() => {
    if (!stateDomain) return;
    let cancelled = false;
    loadedFor.current = null;
    (async () => {
      try {
        const saved = await getGeoState(stateDomain, 'tracking');
        if (cancelled) return;
        setTracked(saved && Array.isArray(saved.items) ? saved.items : []);
      } catch (e) { console.error('Failed to load tracked prompts:', e); }
      finally { if (!cancelled) loadedFor.current = stateDomain; }
    })();
    return () => { cancelled = true; };
  }, [stateDomain]);
  // Save whenever the list changes (after it has loaded), debounced.
  useEffect(() => {
    if (!stateDomain || loadedFor.current !== stateDomain) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      saveGeoState(stateDomain, 'tracking', { items: tracked, savedAt: new Date().toISOString() }).catch((e) => console.error('Failed to save tracked prompts:', e));
    }, 800);
    return () => { if (saveTimer.current) clearTimeout(saveTimer.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tracked, stateDomain]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [showAdd, setShowAdd] = useState(false);
  const [newPrompt, setNewPrompt] = useState('');
  const [newEngines, setNewEngines] = useState(['google_ai', 'perplexity', 'chatgpt']);
  const [scanningId, setScanningId] = useState(null);
  const [scanError, setScanError] = useState(null);
  const [usage, setUsage] = useState(null);
  const [availableEngines, setAvailableEngines] = useState([]);

  const ITEMS_PER_PAGE = 5;

  useEffect(() => {
    const loadMeta = async () => {
      try {
        const engData = await getGeoEngines();
        if (engData && engData.engines) setAvailableEngines(engData.engines);
      } catch (e) { /* */ }
      try {
        const usageData = await getGeoUsage();
        if (usageData) setUsage(usageData);
      } catch (e) { /* */ }
    };
    loadMeta();
  }, []);

  const addPrompt = () => {
    if (!newPrompt.trim()) return;
    const newItem = {
      id: Date.now(),
      prompt: newPrompt.trim(),
      status: 'pending',
      engines: {},
      lastChecked: null,
      mentions: 0,
      citations: 0,
      results: null
    };
    setTracked(prev => [newItem, ...prev]);
    setNewPrompt('');
    setShowAdd(false);
  };

  const removePrompt = (id) => {
    setTracked(prev => prev.filter(t => t.id !== id));
  };

  const scanPrompt = async (item) => {
    const domain = selectedDomain || 'serpfinity.ai';
    setScanningId(item.id);
    setScanError(null);

    try {
      const result = await runSingleScan(item.prompt, domain, newEngines);
      if (result && result.data) {
        const scanData = result.data;
        const engines = {};
        (scanData.engines || []).forEach(eng => {
          engines[eng.engine] = eng.mentioned || false;
        });
        setTracked(prev => prev.map(t => t.id === item.id ? {
          ...t,
          status: 'active',
          engines,
          lastChecked: new Date().toISOString(),
          mentions: scanData.mentions || 0,
          citations: (scanData.engines || []).filter(e => e.cited).length,
          results: scanData
        } : t));
      }
      // Refresh usage
      try {
        const usageData = await getGeoUsage();
        if (usageData) setUsage(usageData);
      } catch (e) { /* */ }
    } catch (err) {
      console.error('Scan failed:', err);
      setScanError(err.message);
      setTracked(prev => prev.map(t => t.id === item.id ? { ...t, status: 'error' } : t));
    } finally {
      setScanningId(null);
    }
  };

  const scanAll = async () => {
    for (const item of tracked) {
      if (item.status !== 'scanning') {
        await scanPrompt(item);
      }
    }
  };

  // Filtering
  const filtered = tracked.filter(t => {
    const matchSearch = !search || t.prompt.toLowerCase().includes(search.toLowerCase());
    const matchStatus = statusFilter === 'all' || t.status === statusFilter;
    return matchSearch && matchStatus;
  });

  const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE);
  const paginated = filtered.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE);

  const breadcrumbItems = [
    { label: 'Dashboard', href: '/dashboard' },
    { label: 'GEO', href: '/geo' },
    { label: 'Tracking' }
  ];

  return (
    <>
      <Helmet>
        <title>GEO Tracking | Prompt Monitoring | SERPfinity™</title>
        <meta name="description" content="Track and monitor your brand prompts across AI search engines. Manage tracked prompts, scan frequency, and AI engine coverage with SERPfinity™." />
        <link rel="canonical" href="https://thedigitalhall.club/geo/tracking" />
        <meta property="og:title" content="GEO Tracking | Prompt Monitoring | SERPfinity™" />
        <meta property="og:url" content="https://thedigitalhall.club/geo/tracking" />
        <meta name="robots" content="index, follow" />
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": "GEO Tracking",
            "description": "Track prompts across AI search engines with SERPfinity™.",
            "url": "https://thedigitalhall.club/geo/tracking"
          }
        `}</script>
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <GEOBreadcrumb items={breadcrumbItems} />

          <header className="mb-6 flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">GEO Tracking</h1>
          <ExportCsvButton data={tracked} filename="geo-tracking" />
              <p className="mt-1 text-gray-600">Manage and scan prompts across AI search engines.</p>
            </div>
            <div className="flex items-center gap-3">
              {usage && (
                <span className="text-sm text-gray-500">
                  <span className="font-medium">{usage.monthlyApiCalls || 0}</span> API calls this month
                </span>
              )}
              {tracked.length > 0 && (
                <button
                  onClick={scanAll}
                  disabled={scanningId !== null}
                  className="inline-flex items-center gap-1.5 px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                  aria-label="Scan all tracked prompts"
                >
                  <ArrowPathIcon className="h-4 w-4" />
                  Scan All
                </button>
              )}
              <button
                onClick={() => setShowAdd(true)}
                className="inline-flex items-center gap-1.5 px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700"
                aria-label="Add new prompt to track"
              >
                <PlusIcon className="h-4 w-4" />
                Add Prompt
              </button>
            </div>
          </header>

          {/* Error alert */}
          {scanError && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2" role="alert">
              <ExclamationTriangleIcon className="h-5 w-5 text-red-500 flex-shrink-0" />
              <p className="text-sm text-red-700">{scanError}</p>
              <button onClick={() => setScanError(null)} className="ml-auto text-red-400 hover:text-red-600" aria-label="Dismiss error">
                <XMarkIcon className="h-4 w-4" />
              </button>
            </div>
          )}

          {/* Add prompt modal */}
          {showAdd && (
            <div className="mb-6 bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Add New Prompt</h2>
              <div className="mb-4">
                <label htmlFor="new-prompt" className="block text-sm font-medium text-gray-700 mb-1">Prompt text</label>
                <input
                  id="new-prompt"
                  type="text"
                  value={newPrompt}
                  onChange={(e) => setNewPrompt(e.target.value)}
                  placeholder="e.g. best AI SEO tools"
                  className="w-full max-w-lg px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
                  onKeyDown={(e) => e.key === 'Enter' && addPrompt()}
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Engines to scan</label>
                <div className="flex flex-wrap gap-2">
                  {AI_ENGINES.map(eng => (
                    <button
                      key={eng.key}
                      onClick={() => setNewEngines(prev => prev.includes(eng.key) ? prev.filter(e => e !== eng.key) : [...prev, eng.key])}
                      className={`px-3 py-1 rounded-full text-sm border ${newEngines.includes(eng.key) ? 'bg-indigo-100 border-indigo-300 text-indigo-700' : 'bg-gray-50 border-gray-200 text-gray-500'}`}
                      aria-pressed={newEngines.includes(eng.key)}
                    >
                      {eng.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex gap-2">
                <button onClick={addPrompt} className="px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700">Add Prompt</button>
                <button onClick={() => { setShowAdd(false); setNewPrompt(''); }} className="px-4 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-200">Cancel</button>
              </div>
            </div>
          )}

          {/* Search and filter */}
          <div className="flex items-center gap-4 mb-4">
            <div className="relative flex-1 max-w-sm">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                placeholder="Search prompts..."
                className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
                aria-label="Search tracked prompts"
              />
            </div>
            <div className="flex gap-1" role="group" aria-label="Filter by status">
              {['all', 'active', 'pending', 'error'].map(s => (
                <button
                  key={s}
                  onClick={() => { setStatusFilter(s); setPage(1); }}
                  className={`px-3 py-1.5 text-sm rounded-lg ${statusFilter === s ? 'bg-indigo-100 text-indigo-700 font-medium' : 'text-gray-500 hover:bg-gray-100'}`}
                  aria-pressed={statusFilter === s}
                >
                  {s.charAt(0).toUpperCase() + s.slice(1)}
                </button>
              ))}
            </div>
          </div>

          {/* Empty state */}
          {tracked.length === 0 && !showAdd && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
              <MagnifyingGlassIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-gray-700 mb-2">No Prompts Tracked</h2>
              <p className="text-gray-500 mb-4">Add prompts to monitor how your brand appears across AI search engines.</p>
              <button
                onClick={() => setShowAdd(true)}
                className="inline-flex items-center gap-1.5 px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700"
              >
                <PlusIcon className="h-4 w-4" />
                Add Your First Prompt
              </button>
            </div>
          )}

          {/* Tracking table */}
          {tracked.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <table className="w-full" aria-label="Tracked prompts">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Prompt</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
                    {AI_ENGINES.map(eng => (
                      <th key={eng.key} className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase">{eng.label}</th>
                    ))}
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Mentions</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Citations</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {paginated.map(item => (
                    <tr key={item.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="text-sm font-medium text-gray-900">{item.prompt}</div>
                        {item.lastChecked && (
                          <div className="text-xs text-gray-400 mt-0.5">
                            Last: {new Date(item.lastChecked).toLocaleDateString()}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[item.status] || 'bg-gray-100 text-gray-600'}`}>
                          {scanningId === item.id ? 'scanning...' : item.status}
                        </span>
                      </td>
                      {AI_ENGINES.map(eng => (
                        <td key={eng.key} className="px-3 py-3 text-center">
                          {item.engines[eng.key] === true ? (
                            <CheckCircleIcon className="h-5 w-5 text-green-500 mx-auto" aria-label={`Found on ${eng.label}`} />
                          ) : item.engines[eng.key] === false ? (
                            <XCircleIcon className="h-5 w-5 text-red-400 mx-auto" aria-label={`Not found on ${eng.label}`} />
                          ) : (
                            <ClockIcon className="h-5 w-5 text-gray-300 mx-auto" aria-label={`Not scanned on ${eng.label}`} />
                          )}
                        </td>
                      ))}
                      <td className="px-4 py-3 text-center text-sm font-medium text-gray-700">{item.mentions}</td>
                      <td className="px-4 py-3 text-center text-sm font-medium text-gray-700">{item.citations}</td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex items-center justify-center gap-1">
                          <button
                            onClick={() => scanPrompt(item)}
                            disabled={scanningId !== null}
                            className="p-1.5 text-indigo-600 hover:bg-indigo-50 rounded disabled:opacity-50"
                            aria-label={`Scan ${item.prompt}`}
                            title="Run scan"
                          >
                            {scanningId === item.id ? (
                              <ArrowPathIcon className="h-4 w-4 animate-spin" />
                            ) : (
                              <PlayIcon className="h-4 w-4" />
                            )}
                          </button>
                          <button
                            onClick={() => removePrompt(item.id)}
                            className="p-1.5 text-red-400 hover:bg-red-50 rounded"
                            aria-label={`Remove ${item.prompt}`}
                            title="Remove prompt"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200">
                  <p className="text-sm text-gray-500">Showing {((page - 1) * ITEMS_PER_PAGE) + 1} to {Math.min(page * ITEMS_PER_PAGE, filtered.length)} of {filtered.length}</p>
                  <div className="flex gap-1">
                    <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="p-1.5 rounded hover:bg-gray-100 disabled:opacity-30" aria-label="Previous page">
                      <ChevronLeftIcon className="h-4 w-4" />
                    </button>
                    <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="p-1.5 rounded hover:bg-gray-100 disabled:opacity-30" aria-label="Next page">
                      <ChevronRightIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
